// turnkey-signing-capability.ts - the pure AS-02 (DS2-0112) signing-capability logic,
// kept import-free (no DOM, no session, no @bounded-sh/core) so it is unit-testable.
//
// The hosted Turnkey signer refuses to sign unless the request carries an issuer-signed
// SIGNING CAPABILITY bound to the app id, the requesting origin, and the EXACT payload
// hash. `requestTurnkeySigningCapability` mints one for the given bytes and
// `buildTurnkeySignRequest` assembles the signer request that carries it. The bridge
// (turnkey-signer-bridge.ts) supplies the base url + id token and routes through both,
// so a regression here (a dropped capability, a wrong route, a swallowed error) is
// caught without a browser.
// POST the Bounded id token + payload hash to the issuer's capability mint route and
// return the issuer-signed capability for exactly these bytes. Credential-free: identity
// is proven with the id token in the body (not an ambient cookie the cross-origin fetch
// would not carry). Throws with the same stable messages the bridge relied on inline.
export async function requestTurnkeySigningCapability(opts) {
    const base = opts.baseUrl.replace(/\/$/, '');
    let res;
    try {
        res = await opts.fetchImpl(`${base}/wallet/turnkey/capability`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ idToken: opts.idToken, messageHex: opts.messageHex }),
        });
    }
    catch (_a) {
        throw new Error('Could not reach the Bounded signer to authorize this signature.');
    }
    if (!res.ok) {
        let error = 'signing_capability_denied';
        try {
            const d = (await res.json());
            if (d && typeof d.error === 'string')
                error = d.error;
        }
        catch (_b) {
            /* keep the default marker */
        }
        throw new Error(`Turnkey signing was not authorized (${error}).`);
    }
    const d = (await res.json());
    if (!d || typeof d.capability !== 'string' || !d.capability) {
        throw new Error('Turnkey signing capability was malformed.');
    }
    return d.capability;
}
// Assemble the `bounded:turnkey:sign` postMessage request, carrying the issuer-signed
// capability for the same bytes. Without the capability the hosted signer refuses
// (capability_required), so it is a required field of every sign request.
// `authorization` is the issuer-signed createWallet action authorization: inline
// (authMode:"turnkey") sessions plant no Better Auth cookie on the issuer, so the
// signer page's same-origin status/create fetches resolve the session from this
// bearer proof instead. The sign approval itself stays capability-gated.
export function buildTurnkeySignRequest(opts) {
    return {
        type: 'bounded:turnkey:sign',
        requestId: opts.requestId,
        messageHex: opts.messageHex,
        capability: opts.capability,
        authorization: opts.authorization,
    };
}
// Mint the authorization required for wallet metadata reads and get-or-create. This uses the
// same credential-free posture as signing capabilities: the Bounded id token proves the user
// and app, while the issuer binds the authorization to the registered requesting origin and
// exact action. A wallet address is intentionally not required for createWallet because this
// request is what provisions the first wallet.
export async function requestTurnkeyActionAuthorization(opts) {
    const base = opts.baseUrl.replace(/\/$/, '');
    let res;
    try {
        res = await opts.fetchImpl(`${base}/wallet/turnkey/action-authorization`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ idToken: opts.idToken, action: opts.action }),
        });
    }
    catch (_a) {
        throw new Error('Could not reach the Bounded signer to authorize this wallet action.');
    }
    if (!res.ok) {
        let error = 'action_authorization_denied';
        try {
            const d = (await res.json());
            if (d && typeof d.error === 'string')
                error = d.error;
        }
        catch (_b) {
            /* keep the default marker */
        }
        throw new Error(`Turnkey wallet action was not authorized (${error}).`);
    }
    const d = (await res.json());
    if (!d || typeof d.authorization !== 'string' || !d.authorization) {
        throw new Error('Turnkey wallet action authorization was malformed.');
    }
    return d.authorization;
}
// Assemble the exact signer request covered by the action authorization. Keep this mapping
// centralized so a future caller cannot mint one action and accidentally post another.
export function buildTurnkeyActionRequest(opts) {
    if (opts.action !== 'getAddress' && opts.action !== 'createWallet') {
        throw new Error('Unsupported Turnkey wallet action.');
    }
    return {
        type: `bounded:turnkey:${opts.action}`,
        requestId: opts.requestId,
        authorization: opts.authorization,
    };
}
